import { useAuth0 } from '@auth0/auth0-react';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import { useQuery } from 'react-query';
import { useLocalStorage } from 'react-use';

import useRegistration from './useRegistration';

import { clearPopupsSS } from '@/modules/dashboard/components/PopupImageVIdeo';
import {
  checkLoggedIn,
  isShouldCheckLoggedInNative,
  setShouldCheckLoggedInNative,
} from '@/services/auth0';
import { userDataAtom, userLoggedInViaOTPAtom } from '@/store';
import { UserInitialData, Maybe } from '@/types';
import { isAndroid } from '@/utils/functions';

declare global {
  interface Window {
    /**
     * Bridge entre la app nativa y la web.
     * Notifica a la app nativa que se ha hecho logout.
     * https://medium.com/@sreeharikv112/communication-from-webview-to-native-ios-android-app-6d842cefe02d
     */
    JSBridge?: {
      notifyMessage: (message: string) => void;
      getInformation: (auth: string, key: string) => string | null;
    };
  }
}

/**
 * useAuth
 * <br/>
 * Determina si está logueado. Da herramientas para modificar ese estado de login.
 * <br/>
 * Puede estar logueado via:
 * - auth0
 * - prelogueado (android + OTP anterior)
 * - OTP (hashLogin)
 * <br/>
 * Interactúa con AuthenticatedWrapper:
 *  -> Authenticated / AuthenticatedOTP / AuthenticatedNativo.
 * Para completar los datos iniciales del usuario.
 */
const useAuth = () => {
  const checkLoggedInJava = isShouldCheckLoggedInNative();
  const [localStorageUser0, setLocalStorageUser0, removeLocalStorageUser0] =
    useLocalStorage<string | null>('auth0-user', null);

  const { handleResetRegisterData } = useRegistration();
  const [userState, setUserState] = useAtom(userDataAtom);
  const { logout, isAuthenticated, isLoading, ...rest } = useAuth0();
  const [stateLoginViaOTP, setStateLoginViaOTP] = useAtom(
    userLoggedInViaOTPAtom
  );

  const handleLogout = () => {
    clearPopupsSS();
    setShouldCheckLoggedInNative(false);
    // eslint-disable-next-line no-console
    console.log('logout-useAuth-handleLogout');
    window.JSBridge?.notifyMessage('logout');
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
    handleResetRegisterData();
    removeLocalStorageUser0();
  };

  const setUserInitialData = (data: Maybe<UserInitialData>) => {
    setUserState(data);
  };

  const isAndroid2 = isAndroid();
  const auth0Loaded = !isLoading;

  const tmpResult = {
    isLoading,
    isAuthenticated,
  };
  if (isAndroid2 && isAuthenticated) {
    // solo para poder debuggear la situación sin usar android
    // deslogueo sólo la parte de auth0, queda la sesión
    for (const key in localStorage) {
      localStorage.removeItem(key);
    }
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });
  }

  const fnCheckLoggedIn = useCallback(async () => await checkLoggedIn(), []);

  const shouldCheckLoggedInNative =
    auth0Loaded && !isAuthenticated && (isAndroid2 || checkLoggedInJava);
  // eslint-disable-next-line no-console
  // console.log(` shouldCheckLoggedInNative: ${shouldCheckLoggedInNative}`);
  const { data: dataIsLogged, isLoading: isLoadingRefreshInitialData } =
    useQuery(['checkLoggedIn'], fnCheckLoggedIn, {
      refetchOnWindowFocus: false,
      keepPreviousData: false,
      enabled: shouldCheckLoggedInNative,
      staleTime: 25 * 60 * 1000,
      cacheTime: 25 * 60 * 1000,
    });

  if (shouldCheckLoggedInNative) {
    tmpResult.isLoading = isLoadingRefreshInitialData;
    tmpResult.isAuthenticated = dataIsLogged?.estado || false;
    /* if (!isLoading && isAuthenticated) {
      setLocalStorageUser0(JSON.stringify(userFake));
    } */
  } else if (
    !tmpResult.isAuthenticated &&
    stateLoginViaOTP.isUsingLoginViaHash
  ) {
    tmpResult.isAuthenticated = stateLoginViaOTP.isLoggedInViaHash;
    tmpResult.isLoading = stateLoginViaOTP.isLoading;
  }

  return {
    ...rest,
    ...tmpResult,
    logout: handleLogout,
    setUserInitialData,
    userInitialData: userState,
    setLocalStorageUser0,
    removeLocalStorageUser0,
    isLocalStorageUser: Boolean(localStorageUser0),
    setStateLoginViaOTP,
  };
};

export default useAuth;
